import { useEffect, useState } from 'react';
import {
  Bot,
  Box,
  FolderOpen,
  History,
  Layers,
  Monitor,
  Moon,
  MessageSquare,
  Package,
  Server,
  Sun,
  Wrench,
} from 'lucide-react';
import { applyTelegramTheme, getThemeMode, setThemeMode, tg, type ThemeMode } from './lib/tg.js';
import { api } from './lib/api.js';
import { wsClient } from './lib/ws.js';
import { SandboxesView } from './views/sandboxes.js';
import { AgentsView } from './views/agents.js';
import { SessionsView } from './views/sessions.js';
import { ChatView } from './views/chat.js';
import { McpsView } from './views/mcps.js';
import { MarkdownEntityView } from './views/markdown_entities.js';
import { SkillsView } from './views/skills.js';
import { ProductsView } from './views/products.js';
import { ProjectsView } from './views/projects.js';
import { AlertsView } from './views/alerts.js';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Toaster } from '@/components/ui/sonner';
import { ErrorBanner } from '@/components/feedback';

type ViewName =
  | 'sandboxes'
  | 'agents'
  | 'sessions'
  | 'chat'
  | 'mcps'
  | 'skills'
  | 'subagents'
  | 'products'
  | 'projects'
  | 'all-projects'
  | 'alerts';

type View =
  | { name: Exclude<ViewName, 'chat' | 'projects'> }
  | { name: 'chat'; session_id: string }
  | { name: 'projects'; product_id: string; product_name: string };

interface Me {
  telegram_user_id: number;
  authorized: boolean;
  dev_bypass?: boolean;
}

const NAV_ITEMS: Array<{
  key: Exclude<ViewName, 'chat' | 'projects'>;
  label: string;
  Icon: typeof Box;
}> = [
  { key: 'sandboxes', label: 'Sandboxes', Icon: Server },
  { key: 'agents', label: 'Agents', Icon: Bot },
  { key: 'sessions', label: 'Sessions', Icon: History },
  { key: 'mcps', label: 'MCPs', Icon: Wrench },
  { key: 'skills', label: 'Skills', Icon: Layers },
  { key: 'subagents', label: 'Subagents', Icon: Box },
  { key: 'products', label: 'Products', Icon: Package },
  { key: 'all-projects', label: 'Projects', Icon: FolderOpen },
  { key: 'alerts', label: 'Alerts', Icon: MessageSquare },
];

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ name: 'sandboxes' });

  useEffect(() => {
    tg()?.ready();
    tg()?.expand();
    applyTelegramTheme();
    tg()?.onEvent?.('themeChanged', applyTelegramTheme);
    void api
      .get<Me>('/api/me')
      .then(setMe)
      .catch((err) => setError(err.message ?? 'auth failed'));
    wsClient.start();
    return () => wsClient.stop();
  }, []);

  if (error) {
    return (
      <Centered>
        <ErrorBanner>{error}</ErrorBanner>
      </Centered>
    );
  }

  if (!me) {
    return (
      <Centered>
        <div className="py-12 text-center text-sm text-muted-foreground">loading…</div>
      </Centered>
    );
  }

  if (!me.authorized) {
    return (
      <Centered>
        <ErrorBanner>not authorized — open this MiniApp from your Telegram bot</ErrorBanner>
      </Centered>
    );
  }

  const isActive = (key: Exclude<ViewName, 'chat' | 'projects'>): boolean =>
    view.name === key || (key === 'products' && view.name === 'projects');

  const currentLabel = (() => {
    if (view.name === 'chat') return 'Chat';
    if (view.name === 'projects') return `${view.product_name} · Projects`;
    return NAV_ITEMS.find((i) => i.key === view.name)?.label ?? '';
  })();

  return (
    <div className="app-root">
      <SidebarProvider defaultOpen>
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <div className="flex items-center gap-2 px-2 py-1">
              <FolderOpen className="size-5 shrink-0" />
              <div className="flex flex-col group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-semibold leading-tight">control</span>
                <span className="text-[10px] leading-tight text-muted-foreground">
                  control plane
                </span>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {NAV_ITEMS.map(({ key, label, Icon }) => (
                    <NavMenuItem
                      key={key}
                      label={label}
                      Icon={Icon}
                      active={isActive(key)}
                      onSelect={() => setView({ name: key })}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <ThemeToggle />
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
              tg id {me.telegram_user_id}
            </div>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
            <SidebarTrigger />
            <h1 className="text-sm font-semibold">{currentLabel}</h1>
          </header>

          {/* Chat needs full-height layout; everything else gets the padded
            * scrollable container. */}
          {view.name === 'chat' ? (
            <ChatView
              sessionId={view.session_id}
              onBack={() => setView({ name: 'sessions' })}
              onNavigate={(target) => {
                if (target === 'sandboxes' || target === 'skills' || target === 'mcps' || target === 'agents') {
                  setView({ name: target });
                }
              }}
            />
          ) : (
            <div className="mx-auto w-full max-w-5xl px-4 py-4 md:px-6 md:py-6">
              {view.name === 'sandboxes' && <SandboxesView />}
              {view.name === 'agents' && (
                <AgentsView onOpenSession={(sid) => setView({ name: 'chat', session_id: sid })} />
              )}
              {view.name === 'sessions' && (
                <SessionsView onOpen={(sid) => setView({ name: 'chat', session_id: sid })} />
              )}
              {view.name === 'mcps' && <McpsView />}
              {view.name === 'skills' && <SkillsView />}
              {view.name === 'subagents' && <MarkdownEntityView prefix="subagents" title="Subagents" />}
              {view.name === 'products' && (
                <ProductsView
                  onOpenProjects={(id, name) =>
                    setView({ name: 'projects', product_id: id, product_name: name })
                  }
                />
              )}
              {view.name === 'projects' && (
                <ProjectsView
                  productId={view.product_id}
                  productName={view.product_name}
                  onBack={() => setView({ name: 'products' })}
                />
              )}
              {view.name === 'all-projects' && <ProjectsView />}
              {view.name === 'alerts' && <AlertsView />}
            </div>
          )}
        </SidebarInset>
      </SidebarProvider>

      <Toaster position="top-center" />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="app-root">
      <div className="mx-auto max-w-3xl px-4 py-8">{children}</div>
    </div>
  );
}

/**
 * Three-way theme toggle in the sidebar footer. Hidden when the sidebar
 * collapses to icon mode (the icons would be too cramped).
 */
function ThemeToggle() {
  const [mode, setMode] = useState<ThemeMode>(() => getThemeMode());
  const update = (next: ThemeMode) => {
    setMode(next);
    setThemeMode(next);
  };
  const opts: Array<{ value: ThemeMode; Icon: typeof Box; label: string }> = [
    { value: 'light', Icon: Sun, label: 'Light' },
    { value: 'system', Icon: Monitor, label: 'System' },
    { value: 'dark', Icon: Moon, label: 'Dark' },
  ];
  return (
    <div className="flex w-full gap-0.5 rounded-md border bg-background p-0.5 group-data-[collapsible=icon]:hidden">
      {opts.map(({ value, Icon, label }) => (
        <button
          key={value}
          type="button"
          onClick={() => update(value)}
          aria-label={label}
          aria-pressed={mode === value}
          className={
            mode === value
              ? 'flex flex-1 items-center justify-center rounded-sm bg-secondary p-1.5 text-secondary-foreground'
              : 'flex flex-1 items-center justify-center rounded-sm p-1.5 text-muted-foreground hover:text-foreground'
          }
          title={label}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </div>
  );
}

/**
 * Sidebar menu entry that auto-closes the mobile sheet after selection.
 * Lives inside SidebarProvider so it can read `isMobile` + `setOpenMobile`
 * via the context.
 */
function NavMenuItem({
  label,
  Icon,
  active,
  onSelect,
}: {
  label: string;
  Icon: typeof Box;
  active: boolean;
  onSelect: () => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        tooltip={label}
        onClick={() => {
          onSelect();
          if (isMobile) setOpenMobile(false);
        }}
      >
        <Icon />
        <span>{label}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
